import React from "react"
import moment from "moment"
import { capitalizeFirstLetter, getOptionTitle } from "../../utils/AnswerUtils"
import { GenderIdentityChoices } from "../../utils/GenderIdentityChoices"
import { Address, Partner } from "../../types/Employee"
import { RoutingInfoQuestions } from "./RoutingInfoQuestions"
import { RoutingInfo } from "../../types/RoutingInfo"
import { AboutYouInfoQuestionKey } from "../../utils/RoutingInfoQuestionKey"

function formatDate(dateStr): string {
  const date = moment(dateStr)
  if (date.isValid()) {
    return date.format("MMMM D, YYYY")
  }
  return null
}

function formatAddress(address: Address): string {
  if (!address) return ""
  if (address.internationalCountry) {
    const internationalAddressElements = [
      address.internationalAddressLine,
      address.city,
      address.state,
      address.zip,
      address.internationalCountry
    ]
    const notEmpty = (e) => e
    return internationalAddressElements.filter(notEmpty).join(", ")
  }
  if (address.nonUsaAddress) return address.nonUsaAddress

  const addressElements = [
    address.address1,
    address.address2,
    address.city,
    address.state,
    address.zip,
    "United States"
  ]
  const notEmpty = (e) => e

  return addressElements.filter(notEmpty).join(", ")
}

type AboutYouDetailProps = {
  routingInfo: RoutingInfo
  partner?: Partner
}

export default function AboutYouDetail({ routingInfo, partner }: AboutYouDetailProps): JSX.Element {
  const aboutYou = routingInfo?.aboutYouInfo
  const skipFields = aboutYou?.skipFields
  if (aboutYou) {
    aboutYou.address.nonUsaAddress = aboutYou.nonUsaAddress
  }

  const aboutYouInfo = [
    {
      questionKey: AboutYouInfoQuestionKey.DateOfBirth,
      hasAnswer: aboutYou?.dateOfBirth !== null,
      question: "When were you born?",
      answer: <>{formatDate(aboutYou?.dateOfBirth)}</>
    },
    {
      questionKey: AboutYouInfoQuestionKey.Sex,
      hasAnswer: aboutYou?.sex !== null,
      question: "What’s your sex?",
      answer: <>{capitalizeFirstLetter(aboutYou?.sex)}</>
    },
    {
      questionKey: AboutYouInfoQuestionKey.GenderIdentity,
      hasAnswer: aboutYou?.genderIdentity !== null,
      question: "What’s your gender identity?",
      answer: (
        <>
          {getOptionTitle(GenderIdentityChoices, aboutYou?.genderIdentity)}
          {aboutYou?.genderIdentityOther && `: ${aboutYou?.genderIdentityOther}`}
        </>
      )
    },
    {
      questionKey: AboutYouInfoQuestionKey.Address,
      hasAnswer: aboutYou?.address ? !Object?.values(aboutYou?.address).every((o) => o === null) : false,
      question: "What’s your home address?",
      answer: <>{formatAddress(aboutYou?.address)}</>
    },
    {
      questionKey: AboutYouInfoQuestionKey.Partner,
      hasAnswer: true,
      question: "Are you on your journey with a partner?",
      answer: <>{partner ? "Yes" : "No"}</>
    },
    {
      questionKey: AboutYouInfoQuestionKey.PartnerName,
      hasAnswer: partner !== null,
      question: "What is your partner’s legal name?",
      answer: <>{`${aboutYou?.partnerFirstName} ${aboutYou?.partnerLastName}`}</>
    },
    {
      questionKey: AboutYouInfoQuestionKey.PartnerPreferredName,
      hasAnswer: aboutYou?.partnerPreferredName !== null,
      question: "What should we call your partner?",
      answer: <>{capitalizeFirstLetter(aboutYou?.partnerPreferredName)}</>
    },
    {
      questionKey: AboutYouInfoQuestionKey.PartnerEmail,
      hasAnswer: partner && aboutYou?.partnerEmail !== null,
      question: "What’s your partner’s email address?",
      answer: <>{aboutYou?.partnerEmail}</>
    },
    {
      questionKey: AboutYouInfoQuestionKey.PartnerDateOfBirth,
      hasAnswer: partner && aboutYou?.partnerDateOfBirth !== null,
      question: "When was your partner born?",
      answer: <>{formatDate(aboutYou?.partnerDateOfBirth)}</>
    },
    {
      questionKey: AboutYouInfoQuestionKey.PartnerSex,
      hasAnswer: partner && aboutYou?.partnerSex !== null,
      question: "What’s your partner’s sex?",
      answer: <>{capitalizeFirstLetter(aboutYou?.partnerSex)}</>
    },
    {
      questionKey: AboutYouInfoQuestionKey.PartnerGenderIdentity,
      hasAnswer: partner && aboutYou?.partnerGenderIdentity !== null,
      question: "What’s your partner’s gender identity?",
      answer: (
        <>
          {getOptionTitle(GenderIdentityChoices, aboutYou?.partnerGenderIdentity)}
          {aboutYou?.partnerGenderIdentityOther && `: ${aboutYou?.partnerGenderIdentityOther}`}
        </>
      )
    }
  ]

  return (
    <RoutingInfoQuestions
      routingInfoQuestions={aboutYouInfo}
      skipFields={skipFields}
      subHeader={"Personal / Partner info"}
    />
  )
}
